import { useState, useEffect, useCallback, memo } from 'react';
import {
    Home, Users, TestTube, Briefcase, FileText, Phone, Info,
    Save, AlertCircle, CheckCircle, ChevronDown, ChevronRight,
    Type, Image, Star, Megaphone, MessageSquare, MapPin, Globe,
    Heart, Eye, Gem, BookOpen, Loader2, Trash2, AlertTriangle
} from 'lucide-react';
import DoctorManager from './SiteEditor/DoctorManager';
import TestsManager from './SiteEditor/TestsManager';
import ServicesManager from './SiteEditor/ServicesManager';
import BlogManager from './SiteEditor/BlogManager';
import { API_URL } from '../config/api';
import './SiteEditor/SiteEditorStyles.css';
import './SiteEditor.css';

const TABS = [
    { id: 'home', label: 'Inicio', icon: Home },
    { id: 'doctors', label: 'Equipo Médico', icon: Users },
    { id: 'tests', label: 'Pruebas', icon: TestTube },
    { id: 'services', label: 'Servicios', icon: Briefcase },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'contact', label: 'Contacto', icon: Phone },
    { id: 'about', label: 'Nosotros', icon: Info },
];

/* "?"?"? Collapsible Section "?"?"? */
const CollapsibleSection = ({ title, icon: Icon, defaultOpen = false, children, fieldCount }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className={`collapsible-section ${isOpen ? 'open' : ''}`}>
            <button
                type="button"
                className="collapsible-header"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="collapsible-title">
                    {Icon && <Icon size={18} />}
                    <span>{title}</span>
                    {fieldCount !== undefined && (
                        <span className="field-count">{fieldCount} campos</span>
                    )}
                </div>
                <ChevronDown size={20} className={`chevron-icon ${isOpen ? 'rotated' : ''}`} />
            </button>
            {isOpen && (
                <div className="collapsible-body">
                    {children}
                </div>
            )}
        </div>
    );
};

/* "?"?"? ContentField (sin botón guardar individual) "?"?"? */
const ContentField = memo(({ section, keyName, label, type = 'input', rows = 3, maxLength, value, onChange, saving }) => {
    const handleInputChange = (e) => {
        const newValue = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
        onChange(`${section}.${keyName}`, newValue);
    };

    return (
        <div className="content-field">
            <label>
                {label}
                {maxLength && (
                    <span className={`char-counter ${(value || '').length > maxLength * 0.9 ? 'warning' : ''}`}>
                        {(value || '').length}/{maxLength}
                    </span>
                )}
            </label>
            {type === 'textarea' ? (
                <textarea
                    value={value || ''}
                    onChange={handleInputChange}
                    rows={rows}
                    maxLength={maxLength}
                />
            ) : (
                <input
                    type="text"
                    value={value || ''}
                    onChange={handleInputChange}
                    maxLength={maxLength}
                />
            )}
        </div>
    );
});

ContentField.displayName = 'ContentField';

/* "?"?"? Main SiteEditor Component "?"?"? */
const SiteEditor = () => {
    const [activeTab, setActiveTab] = useState('home');
    const [content, setContent] = useState({});
    const [originalContent, setOriginalContent] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirtyFields, setDirtyFields] = useState(new Set());
    const [message, setMessage] = useState({ type: '', text: '' });
    const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });

    useEffect(() => {
        fetchContent();
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (dirtyFields.size > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [dirtyFields]);

    const getImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `${API_URL}${url}`;
    };

    const fetchContent = async () => {
        try {
            const response = await fetch(`${API_URL}/content`);
            const data = await response.json();
            if (data.success) {
                setContent(data.data);
                setOriginalContent(data.data);
            }
        } catch (error) {
            console.error('Error fetching content:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = useCallback((key, value) => {
        setContent(prev => ({ ...prev, [key]: value }));
        setDirtyFields(prev => {
            const next = new Set(prev);
            const originalValue = originalContent[key] ?? '';
            const newValue = value ?? '';
            if (String(newValue) === String(originalValue)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, [originalContent]);

    const handleSave = async (section, keyName, directValue = null) => {
        const token = localStorage.getItem('token');
        const fullKey = `${section}.${keyName}`;
        const valueToSave = directValue !== null ? directValue : content[fullKey];

        const response = await fetch(`${API_URL}/content`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                section,
                key_name: keyName,
                content: valueToSave
            })
        });

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.message || 'Error al guardar');
        }
        return data;
    };

    const handleSaveAll = async () => {
        if (dirtyFields.size === 0) return;

        setSaving(true);
        setMessage({ type: '', text: '' });
        const total = dirtyFields.size;
        setSaveProgress({ current: 0, total });

        let saved = 0;
        let errors = 0;

        for (const fullKey of dirtyFields) {
            const [section, ...rest] = fullKey.split('.');
            const keyName = rest.join('.');

            try {
                await handleSave(section, keyName);
                saved++;
                setSaveProgress({ current: saved, total });
            } catch (error) {
                errors++;
                console.error(`Error saving ${fullKey}:`, error);
            }
        }

        if (errors === 0) {
            setMessage({ type: 'success', text: `${saved} campo${saved > 1 ? 's' : ''} guardado${saved > 1 ? 's' : ''} correctamente` });
            setDirtyFields(new Set());
            setOriginalContent({ ...content });
        } else {
            setMessage({ type: 'error', text: `${saved} guardados, ${errors} con error` });
        }

        setSaving(false);
        setSaveProgress({ current: 0, total: 0 });
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    };

    const handleDiscardChanges = () => {
        setContent({ ...originalContent });
        setDirtyFields(new Set());
        setMessage({ type: '', text: '' });
    };

    const handleImageUpload = async (e, section, keyName) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        const category = section === 'home' ? 'carousel' : 'general';
        formData.append('category', category);

        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const data = await res.json();
            if (data.success) {
                const fullKey = `${section}.${keyName}`;
                const imageUrl = data.data.url;

                handleChange(fullKey, imageUrl);
                await handleSave(section, keyName, imageUrl);

                // Remove from dirty since it was saved immediately
                setDirtyFields(prev => {
                    const next = new Set(prev);
                    next.delete(fullKey);
                    return next;
                });

                setMessage({ type: 'success', text: 'Imagen subida y guardada' });
            } else {
                setMessage({ type: 'error', text: data.message || 'Error al subir imagen' });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setMessage({ type: 'error', text: 'Error de conexión al subir imagen' });
        } finally {
            setSaving(false);
        }
    };

    // Helper to render ContentField
    const renderField = (section, keyName, label, type = 'input', rows = 3, maxLength) => (
        <ContentField
            key={`${section}.${keyName}`}
            section={section}
            keyName={keyName}
            label={label}
            type={type}
            rows={rows}
            maxLength={maxLength}
            value={content[`${section}.${keyName}`]}
            onChange={handleChange}
            saving={saving}
        />
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'home':
                return (
                    <div className="tab-content">
                        <h3>Página de Inicio</h3>

                        <CollapsibleSection title="Hero Principal" icon={Type} defaultOpen={true} fieldCount={2}>
                            {renderField('home', 'hero_title', 'Título Principal', 'input', 3, 80)}
                            {renderField('home', 'hero_subtitle', 'Subtítulo', 'textarea', 2, 200)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Introducción" icon={BookOpen} fieldCount={3}>
                            {renderField('home', 'intro_title', 'Título Introducción', 'input', 3, 60)}
                            {renderField('home', 'intro_text_1', 'Texto Intro 1', 'textarea', 3, 500)}
                            {renderField('home', 'intro_text_2', 'Texto Intro 2', 'textarea', 3, 500)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Carrusel de Imágenes" icon={Image} fieldCount={parseInt(content['home.hero_carousel_count']) || 3}>
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label>Cantidad de Slides</label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={content['home.hero_carousel_count'] || 3}
                                        onChange={(e) => handleChange('home.hero_carousel_count', e.target.value)}
                                        style={{ width: '80px' }}
                                    />
                                </div>
                            </div>

                            {Array.from({ length: parseInt(content['home.hero_carousel_count']) || 3 }).map((_, index) => {
                                const slideNum = index + 1;
                                const imageKey = `hero_slide_${slideNum}_image`;
                                const fullImageKey = `home.${imageKey}`;
                                const hasImage = !!content[fullImageKey];

                                return (
                                    <div key={slideNum} className="slide-editor-card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h5 style={{ margin: 0 }}>Slide {slideNum}</h5>
                                            {!hasImage && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f59e0b', fontSize: '0.8rem', fontWeight: 600 }}>
                                                    <AlertTriangle size={14} />
                                                    Sin imagen ?" no se mostrará en el sitio
                                                </span>
                                            )}
                                        </div>

                                        <div className="form-group" style={{ marginBottom: '15px' }}>
                                            <label>Imagen (Recomendado: 1920x1080px)</label>
                                            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                                                {hasImage && (
                                                    <div className="photo-preview" style={{ width: '200px', height: '112px' }}>
                                                        <img src={getImageUrl(content[fullImageKey])} alt={`Slide ${slideNum}`} />
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                    <input
                                                        type="text"
                                                        value={content[fullImageKey] || ''}
                                                        readOnly
                                                        placeholder="URL de la imagen"
                                                        style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                                                    />
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <label className="upload-btn">
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => handleImageUpload(e, 'home', imageKey)}
                                                            />
                                                            Subir Nueva Imagen
                                                        </label>
                                                        {hasImage && (
                                                            <button
                                                                type="button"
                                                                className="btn-remove-image"
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', gap: '4px',
                                                                    padding: '6px 12px', background: '#fee2e2', color: '#dc2626',
                                                                    border: '1px solid #fecaca', borderRadius: '6px',
                                                                    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500
                                                                }}
                                                                onClick={async () => {
                                                                    if (window.confirm('¿Eliminar esta imagen del slide?')) {
                                                                        handleChange(fullImageKey, '');
                                                                        await handleSave('home', imageKey, '');
                                                                        setDirtyFields(prev => {
                                                                            const next = new Set(prev);
                                                                            next.delete(fullImageKey);
                                                                            return next;
                                                                        });
                                                                        setMessage({ type: 'success', text: 'Imagen eliminada' });
                                                                    }
                                                                }}
                                                            >
                                                                <Trash2 size={14} />
                                                                Quitar
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {renderField('home', `hero_slide_${slideNum}_title`, 'Título', 'input', 1, 60)}
                                        {renderField('home', `hero_slide_${slideNum}_subtitle`, 'Subtítulo', 'textarea', 2, 150)}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            {renderField('home', `hero_slide_${slideNum}_btn_text`, 'Texto Botón', 'input', 1, 20)}
                                            {renderField('home', `hero_slide_${slideNum}_btn_link`, 'Enlace Botón', 'input', 1, 100)}
                                        </div>
                                    </div>
                                );
                            })}
                        </CollapsibleSection>

                        <CollapsibleSection title="Características Principales" icon={Star} fieldCount={6}>
                            <div className="features-grid-editor" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                                <div className="feature-editor-item">
                                    <h5>Característica 1</h5>
                                    {renderField('home', 'feature_1_title', 'Título', 'input', 1, 30)}
                                    {renderField('home', 'feature_1_desc', 'Descripción', 'textarea', 3, 150)}
                                </div>
                                <div className="feature-editor-item">
                                    <h5>Característica 2</h5>
                                    {renderField('home', 'feature_2_title', 'Título', 'input', 1, 30)}
                                    {renderField('home', 'feature_2_desc', 'Descripción', 'textarea', 3, 150)}
                                </div>
                                <div className="feature-editor-item">
                                    <h5>Característica 3</h5>
                                    {renderField('home', 'feature_3_title', 'Título', 'input', 1, 30)}
                                    {renderField('home', 'feature_3_desc', 'Descripción', 'textarea', 3, 150)}
                                </div>
                            </div>
                        </CollapsibleSection>

                        <CollapsibleSection title="Llamada a la Acción (CTA)" icon={Megaphone} fieldCount={3}>
                            {renderField('home', 'cta_title', 'Título CTA', 'input', 1, 60)}
                            {renderField('home', 'cta_text', 'Texto Descriptivo', 'textarea', 2, 120)}
                            {renderField('home', 'cta_button_text', 'Texto Botón', 'input', 1, 20)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Testimonios" icon={MessageSquare} fieldCount={6}>
                            <div className="testimonials-editor">
                                <div className="testimonial-item" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                    <h5>Testimonio 1</h5>
                                    {renderField('home', 'testimonial_1_text', 'Opinión', 'textarea', 2, 300)}
                                    {renderField('home', 'testimonial_1_author', 'Autor', 'input', 1, 40)}
                                </div>
                                <div className="testimonial-item" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                    <h5>Testimonio 2</h5>
                                    {renderField('home', 'testimonial_2_text', 'Opinión', 'textarea', 2, 300)}
                                    {renderField('home', 'testimonial_2_author', 'Autor', 'input', 1, 40)}
                                </div>
                                <div className="testimonial-item" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                    <h5>Testimonio 3</h5>
                                    {renderField('home', 'testimonial_3_text', 'Opinión', 'textarea', 2, 300)}
                                    {renderField('home', 'testimonial_3_author', 'Autor', 'input', 1, 40)}
                                </div>
                            </div>
                        </CollapsibleSection>
                    </div>
                );

            case 'doctors':
                return <DoctorManager />;

            case 'tests':
                return <TestsManager />;

            case 'services':
                return <ServicesManager />;

            case 'blog':
                return <BlogManager />;

            case 'contact':
                return (
                    <div className="tab-content">
                        <h3>Información de Contacto</h3>

                        <CollapsibleSection title="Datos de Contacto" icon={Phone} defaultOpen={true} fieldCount={6}>
                            {renderField('contact', 'phone', 'Teléfono', 'input', 3, 20)}
                            {renderField('contact', 'email', 'Correo Electrónico', 'input', 3, 60)}
                            {renderField('contact', 'address', 'Dirección', 'input', 3, 150)}
                            {renderField('contact', 'hours', 'Horarios', 'textarea', 2, 100)}
                            {renderField('contact', 'whatsapp', 'WhatsApp (número)', 'input', 3, 15)}
                            {renderField('contact', 'map_url', 'URL del Mapa (Google Maps Embed)', 'textarea', 2, 500)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Pie de Página (Footer)" icon={MapPin} fieldCount={2}>
                            {renderField('contact', 'footer_tagline', 'Frase Corta (Debajo del logo)', 'input', 2, 100)}
                            {renderField('contact', 'footer_description', 'Descripción Pie de Página', 'textarea', 3, 250)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Redes Sociales" icon={Globe} fieldCount={4}>
                            {renderField('contact', 'social_facebook', 'Facebook URL', 'input', 1, 200)}
                            {renderField('contact', 'social_instagram', 'Instagram URL', 'input', 1, 200)}
                            {renderField('contact', 'social_linkedin', 'LinkedIn URL', 'input', 1, 200)}
                            {renderField('contact', 'social_tiktok', 'TikTok URL', 'input', 1, 200)}
                        </CollapsibleSection>
                    </div>
                );

            case 'about':
                return (
                    <div className="tab-content">
                        <h3>Página Nosotros</h3>

                        <CollapsibleSection title="General" icon={Info} defaultOpen={true} fieldCount={2}>
                            {renderField('about', 'page_title', 'Título de Página', 'input', 3, 50)}
                            {renderField('about', 'page_subtitle', 'Subtítulo', 'textarea', 2, 200)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Filosofía" icon={Gem} fieldCount={5}>
                            {renderField('about', 'philosophy_title', 'Título Filosofía', 'input', 3, 50)}
                            {renderField('about', 'philosophy_text', 'Texto Filosofía', 'textarea', 4, 500)}

                            <div className="form-group" style={{ margin: '20px 0', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#1e293b', fontWeight: '600' }}>
                                    <Image size={18} />
                                    Imagen de Filosofía
                                </label>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                    {content['about.philosophy_image'] && (
                                        <div className="photo-preview" style={{ width: '160px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #e2e8f0', flexShrink: 0 }}>
                                            <img
                                                src={getImageUrl(content['about.philosophy_image'])}
                                                alt="Filosofía"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                                        <label className="upload-btn" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: 'white', color: '#083677', border: '2px solid #083677', borderRadius: '8px', fontWeight: '600', width: 'fit-content', transition: 'all 0.2s' }}>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={(e) => handleImageUpload(e, 'about', 'philosophy_image')}
                                            />
                                            <Image size={18} /> {content['about.philosophy_image'] ? 'Cambiar Imagen' : 'Subir Imagen'}
                                        </label>
                                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                            Recomendado: 800x600px en formato horizontal (JPG, PNG o WEBP). Tamaño máximo: 2MB.
                                        </span>
                                        {content['about.philosophy_image'] && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    handleChange('about.philosophy_image', '');
                                                }}
                                                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.85rem', fontWeight: '500', cursor: 'pointer', textAlign: 'left', padding: '0', display: 'flex', alignItems: 'center', gap: '4px', width: 'fit-content', marginTop: '5px' }}
                                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                            >
                                                <Trash2 size={14} /> Eliminar imagen actual
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <h5 style={{ marginTop: '10px', marginBottom: '15px', color: '#475569' }}>Puntos Clave de la Filosofía</h5>
                            {renderField('about', 'philosophy_point_1', 'Punto 1', 'input', 2, 150)}
                            {renderField('about', 'philosophy_point_2', 'Punto 2', 'input', 2, 150)}
                            {renderField('about', 'philosophy_point_3', 'Punto 3', 'input', 2, 150)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Misión y Visión" icon={Eye} fieldCount={4}>
                            {renderField('about', 'mission_title', 'Título Misión', 'input', 3, 30)}
                            {renderField('about', 'mission_text', 'Texto Misión', 'textarea', 3, 400)}
                            {renderField('about', 'vision_title', 'Título Visión', 'input', 3, 30)}
                            {renderField('about', 'vision_text', 'Texto Visión', 'textarea', 3, 400)}
                        </CollapsibleSection>

                        <CollapsibleSection title="Valores" icon={Heart} fieldCount={2}>
                            {renderField('about', 'values_title', 'Título Valores', 'input', 3, 30)}
                            {renderField('about', 'values_text', 'Valores (uno por línea)', 'textarea', 4, 300)}
                        </CollapsibleSection>
                    </div>
                );

            default:
                return null;
        }
    };

    if (loading) {
        return <div className="site-editor-loading">Cargando editor...</div>;
    }

    return (
        <div className="site-editor-v2">
            <div className="editor-header">
                <div className="editor-title-wrapper">
                    <div className="editor-title">
                        <Globe size={28} />
                        <h2>Editor del Sitio Web</h2>
                    </div>
                    <p>Administra el contenido de tu sitio público</p>
                </div>
            </div>

            {message.text && (
                <div className={`editor-message ${message.type}`}>
                    {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    <span>{message.text}</span>
                </div>
            )}

            <div className="editor-layout">
                <nav className="editor-sidebar">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <tab.icon size={18} />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </nav>

                <main className="editor-main">
                    {renderTabContent()}
                </main>
            </div>

            {/* "?"?"? Sticky Save Bar "?"?"? */}
            {dirtyFields.size > 0 && (
                <div className="sticky-save-bar">
                    <div className="save-bar-content">
                        <div className="save-bar-info">
                            <AlertCircle size={18} />
                            <span>
                                <strong>{dirtyFields.size}</strong> campo{dirtyFields.size > 1 ? 's' : ''} sin guardar
                            </span>
                        </div>
                        <div className="save-bar-actions">
                            <button
                                className="btn-discard"
                                onClick={handleDiscardChanges}
                                disabled={saving}
                            >
                                Descartar
                            </button>
                            <button
                                className="btn-save-all"
                                onClick={handleSaveAll}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 size={16} className="spin" />
                                        Guardando {saveProgress.current}/{saveProgress.total}...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Guardar Todo
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiteEditor;
